import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import {
    Search, Replace, ChevronDown, ChevronUp, X, Check,
    FileText, AlertCircle, ArrowRight, RotateCcw
} from 'lucide-react';

const FindReplace = ({ 
    isOpen, 
    onClose, 
    chapters,
    scenes,
    onUpdateChapter,
    onUpdateScene,
    currentChapterId,
    currentSceneId
}) => {
    const [findText, setFindText] = useState('');
    const [replaceText, setReplaceText] = useState('');
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [wholeWord, setWholeWord] = useState(false);
    const [useRegex, setUseRegex] = useState(false);
    const [searchScope, setSearchScope] = useState('current'); // 'current', 'chapter', 'all'
    const [results, setResults] = useState([]);
    const [currentResultIndex, setCurrentResultIndex] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [replacementCount, setReplacementCount] = useState(0);

    // Reset when dialog opens
    useEffect(() => {
        if (isOpen) {
            setResults([]);
            setCurrentResultIndex(0);
            setReplacementCount(0);
        }
    }, [isOpen]);

    // Build search regex
    const buildSearchRegex = useCallback(() => {
        if (!findText) return null;
        
        try {
            let pattern = findText;
            
            if (!useRegex) {
                // Escape regex special characters
                pattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            }
            
            if (wholeWord) {
                pattern = `\\b${pattern}\\b`;
            }
            
            const flags = caseSensitive ? 'g' : 'gi';
            return new RegExp(pattern, flags);
        } catch (e) {
            toast.error('Invalid search pattern');
            return null;
        }
    }, [findText, caseSensitive, wholeWord, useRegex]);

    // Strip HTML tags for searching
    const stripHtml = (html) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html || '';
        return tmp.textContent || tmp.innerText || '';
    };

    // Search function
    const handleSearch = useCallback(() => {
        if (!findText.trim()) {
            toast.error('Please enter search text');
            return;
        }

        setIsSearching(true);
        const regex = buildSearchRegex();
        if (!regex) {
            setIsSearching(false);
            return;
        }

        const foundResults = [];
        
        // Determine what to search
        let searchItems = [];
        
        if (searchScope === 'current') {
            // Search only current chapter or scene
            if (currentSceneId) {
                const scene = scenes.find(s => s.id === currentSceneId);
                if (scene) searchItems.push({ type: 'scene', item: scene });
            } else if (currentChapterId) {
                const chapter = chapters.find(c => c.id === currentChapterId);
                if (chapter) searchItems.push({ type: 'chapter', item: chapter });
            }
        } else if (searchScope === 'chapter') {
            // Search current chapter and its scenes
            if (currentChapterId) {
                const chapter = chapters.find(c => c.id === currentChapterId);
                if (chapter) searchItems.push({ type: 'chapter', item: chapter });
                
                const chapterScenes = scenes.filter(s => s.chapter_id === currentChapterId);
                chapterScenes.forEach(scene => {
                    searchItems.push({ type: 'scene', item: scene });
                });
            }
        } else {
            // Search all chapters and scenes
            chapters.forEach(chapter => {
                searchItems.push({ type: 'chapter', item: chapter });
            });
            scenes.forEach(scene => {
                searchItems.push({ type: 'scene', item: scene });
            });
        }

        // Perform search
        searchItems.forEach(({ type, item }) => {
            const content = item.content || '';
            const plainText = stripHtml(content);
            let match;
            
            // Reset regex lastIndex
            regex.lastIndex = 0;
            
            while ((match = regex.exec(plainText)) !== null) {
                // Get context around the match
                const start = Math.max(0, match.index - 40);
                const end = Math.min(plainText.length, match.index + match[0].length + 40);
                const context = plainText.substring(start, end);
                
                foundResults.push({
                    type,
                    itemId: item.id,
                    title: item.title,
                    match: match[0],
                    index: match.index,
                    context: (start > 0 ? '...' : '') + context + (end < plainText.length ? '...' : ''),
                    content: content
                });
            }
        });

        setResults(foundResults);
        setCurrentResultIndex(0);
        setIsSearching(false);

        if (foundResults.length === 0) {
            toast.info('No matches found');
        } else {
            toast.success(`Found ${foundResults.length} match${foundResults.length === 1 ? '' : 'es'}`);
        }
    }, [findText, searchScope, chapters, scenes, currentChapterId, currentSceneId, buildSearchRegex]);

    // Navigate between results
    const goToNext = () => {
        if (results.length > 0) {
            setCurrentResultIndex((prev) => (prev + 1) % results.length);
        }
    };

    const goToPrevious = () => {
        if (results.length > 0) {
            setCurrentResultIndex((prev) => (prev - 1 + results.length) % results.length);
        }
    };

    // Replace current match
    const replaceCurrentMatch = async () => {
        if (results.length === 0 || !results[currentResultIndex]) return;
        
        const result = results[currentResultIndex];
        const regex = buildSearchRegex();
        if (!regex) return;

        try {
            // We need to replace in HTML content while preserving structure
            // This is a simplified approach - replace first occurrence
            const content = result.content;
            const newContent = content.replace(regex, replaceText);
            
            if (result.type === 'chapter') {
                await onUpdateChapter(result.itemId, { content: newContent });
            } else {
                await onUpdateScene(result.itemId, { content: newContent });
            }

            // Remove this result from the list
            const newResults = [...results];
            newResults.splice(currentResultIndex, 1);
            setResults(newResults);
            
            // Adjust current index if needed
            if (currentResultIndex >= newResults.length && newResults.length > 0) {
                setCurrentResultIndex(newResults.length - 1);
            }
            
            setReplacementCount(prev => prev + 1);
            toast.success('Replaced 1 match');
        } catch (error) {
            toast.error('Failed to replace');
        }
    };

    // Replace all matches
    const replaceAllMatches = async () => {
        if (results.length === 0) return;
        
        const regex = buildSearchRegex();
        if (!regex) return;

        try {
            // Group results by item
            const itemUpdates = new Map();
            
            results.forEach(result => {
                const key = `${result.type}-${result.itemId}`;
                if (!itemUpdates.has(key)) {
                    itemUpdates.set(key, {
                        type: result.type,
                        itemId: result.itemId,
                        content: result.content
                    });
                }
            });

            // Apply replacements
            let totalReplaced = 0;
            
            for (const [, update] of itemUpdates) {
                // Count matches in this item
                const matches = update.content.match(regex);
                const matchCount = matches ? matches.length : 0;
                
                // Replace all occurrences
                const newContent = update.content.replace(new RegExp(regex.source, regex.flags), replaceText);
                
                if (update.type === 'chapter') {
                    await onUpdateChapter(update.itemId, { content: newContent });
                } else {
                    await onUpdateScene(update.itemId, { content: newContent });
                }
                
                totalReplaced += matchCount;
            }

            setReplacementCount(prev => prev + totalReplaced);
            setResults([]);
            toast.success(`Replaced ${totalReplaced} match${totalReplaced === 1 ? '' : 'es'}`);
        } catch (error) {
            toast.error('Failed to replace all');
        }
    };

    // Highlight match in context
    const highlightMatch = (context, match) => {
        if (!match) return context;
        const regex = new RegExp(`(${match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = context.split(regex);
        return parts.map((part, i) => 
            regex.test(part) ? <mark key={i} className="bg-accent/30 text-foreground px-0.5 rounded">{part}</mark> : part
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Search className="h-5 w-5 text-accent" />
                        Find & Replace
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                    {/* Search Inputs */}
                    <div className="grid gap-4">
                        <div className="grid grid-cols-[1fr,auto] gap-2">
                            <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">Find</Label>
                                <Input
                                    value={findText}
                                    onChange={(e) => setFindText(e.target.value)}
                                    placeholder="Search text..."
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                    data-testid="find-input"
                                />
                            </div>
                            <div className="flex items-end">
                                <Button onClick={handleSearch} disabled={isSearching} data-testid="search-btn">
                                    <Search className="h-4 w-4 mr-2" />
                                    Find
                                </Button>
                            </div>
                        </div>

                        <div className="grid grid-cols-[1fr,auto,auto] gap-2">
                            <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">Replace with</Label>
                                <Input
                                    value={replaceText}
                                    onChange={(e) => setReplaceText(e.target.value)}
                                    placeholder="Replacement text..."
                                    data-testid="replace-input"
                                />
                            </div>
                            <div className="flex items-end">
                                <Button 
                                    variant="outline" 
                                    onClick={replaceCurrentMatch}
                                    disabled={results.length === 0}
                                    data-testid="replace-btn"
                                >
                                    <Replace className="h-4 w-4 mr-2" />
                                    Replace
                                </Button>
                            </div>
                            <div className="flex items-end">
                                <Button 
                                    variant="outline"
                                    onClick={replaceAllMatches}
                                    disabled={results.length === 0}
                                    data-testid="replace-all-btn"
                                >
                                    <Check className="h-4 w-4 mr-2" />
                                    Replace All
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Options */}
                    <div className="flex flex-wrap gap-4 p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-2">
                            <Switch
                                id="case-sensitive"
                                checked={caseSensitive}
                                onCheckedChange={setCaseSensitive}
                            />
                            <Label htmlFor="case-sensitive" className="text-sm">Case sensitive</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                id="whole-word"
                                checked={wholeWord}
                                onCheckedChange={setWholeWord}
                            />
                            <Label htmlFor="whole-word" className="text-sm">Whole word</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                id="use-regex"
                                checked={useRegex}
                                onCheckedChange={setUseRegex}
                            />
                            <Label htmlFor="use-regex" className="text-sm">Regex</Label>
                        </div>
                        <div className="ml-auto">
                            <select
                                value={searchScope}
                                onChange={(e) => setSearchScope(e.target.value)}
                                className="text-sm border rounded px-2 py-1 bg-background"
                                data-testid="search-scope"
                            >
                                <option value="current">Current document</option>
                                <option value="chapter">Current chapter + scenes</option>
                                <option value="all">Entire book</option>
                            </select>
                        </div>
                    </div>

                    {/* Results Header */}
                    {results.length > 0 && (
                        <div className="flex items-center justify-between px-2">
                            <div className="flex items-center gap-2">
                                <Badge variant="secondary">
                                    {results.length} match{results.length === 1 ? '' : 'es'}
                                </Badge>
                                {replacementCount > 0 && (
                                    <Badge variant="outline" className="text-accent border-accent">
                                        {replacementCount} replaced
                                    </Badge>
                                )}
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-sm text-muted-foreground">
                                    {currentResultIndex + 1} of {results.length}
                                </span>
                                <Button variant="ghost" size="sm" onClick={goToPrevious}>
                                    <ChevronUp className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={goToNext}>
                                    <ChevronDown className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Results List */}
                    <ScrollArea className="flex-1 min-h-0">
                        <div className="space-y-2 pr-4">
                            {results.map((result, index) => (
                                <div
                                    key={`${result.itemId}-${result.index}`}
                                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                        index === currentResultIndex 
                                            ? 'border-accent bg-accent/5' 
                                            : 'border-border hover:border-accent/50'
                                    }`}
                                    onClick={() => setCurrentResultIndex(index)}
                                    data-testid={`result-${index}`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <FileText className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-medium text-sm">{result.title}</span>
                                        <Badge variant="outline" className="text-xs">
                                            {result.type}
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {highlightMatch(result.context, result.match)}
                                    </p>
                                </div>
                            ))}

                            {results.length === 0 && findText && !isSearching && (
                                <div className="text-center py-8 text-muted-foreground">
                                    <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                    <p>No matches found</p>
                                    <p className="text-sm">Try adjusting your search options</p>
                                </div>
                            )}

                            {!findText && (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                    <p>Enter text to search</p>
                                    <p className="text-sm">Search across your chapters and scenes</p>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default FindReplace;
